import { useEffect, useMemo, useState } from 'react'
import { Header } from './components/Header'
import { Hero } from './components/Hero'
import { LayerSection } from './components/LayerSection'
import { HomePage } from './components/HomePage'
import { ParadigmsPage } from './components/ParadigmsPage'
import { LabPage } from './components/LabPage'
import { IndustryDetailPage } from './components/IndustryDetailPage'
import { MethodPage } from './components/MethodPage'
import { CasesPage } from './components/CasesPage'
import { PlaybookPage } from './components/PlaybookPage'
import { AutoDemoOverlay } from './components/AutoDemoOverlay'
import { OrionProvider } from './lib/state'
import { AutoDemoProvider, useAutoDemo } from './lib/auto-demo'
import { useT } from './lib/i18n'
import { LAYER_IDS } from './data/layers'
import { INDUSTRY_IDS, type IndustryId } from './data/industryParadigms'
import type { LayerStanza } from './types'

export type View = 'home' | 'paradigms' | 'demo' | 'lab' | 'industries' | 'method' | 'cases' | 'playbook'

export interface Route {
  view: View
  labId?: string
  industryId?: IndustryId
  scrollAnchor?: string
}

const DEFAULT_LAB_ID = 'event-driven'
const INDUSTRY_SET: ReadonlySet<string> = new Set(INDUSTRY_IDS)

function parseHash(hash: string): Route {
  const clean = hash.replace(/^#\/?/, '').toLowerCase()
  const [seg1, seg2] = clean.split(/[/?&]/)
  if (seg1 === 'lab') return { view: 'lab', labId: seg2 || DEFAULT_LAB_ID }
  if (seg1 === 'paradigms') return { view: 'paradigms' }
  if (seg1 === 'demo') return { view: 'demo' }
  if (seg1 === 'method') return { view: 'method' }
  if (seg1 === 'cases') return { view: 'cases' }
  if (seg1 === 'playbook') return { view: 'playbook' }
  if (seg1 === 'industries') {
    return seg2 && INDUSTRY_SET.has(seg2)
      ? { view: 'industries', industryId: seg2 as IndustryId }
      : { view: 'home', scrollAnchor: 'industries' }
  }
  return { view: 'home' }
}

function routeToHash(r: Route): string {
  if (r.view === 'lab') return `#/lab/${r.labId ?? DEFAULT_LAB_ID}`
  if (r.view === 'industries' && r.industryId) return `#/industries/${r.industryId}`
  if (r.view === 'home' && r.scrollAnchor === 'industries') return '#/industries'
  if (r.view === 'home') return ''
  return `#/${r.view}`
}

export default function App() {
  const [route, setRouteState] = useState<Route>(() =>
    typeof window === 'undefined' ? { view: 'home' } : parseHash(window.location.hash),
  )

  useEffect(() => {
    const onHash = () => setRouteState(parseHash(window.location.hash))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // Scroll to anchor when the parsed route carries a scrollAnchor (e.g.
  // direct hit of #/industries or back/forward into that URL).
  useEffect(() => {
    if (!route.scrollAnchor) return
    const id = route.scrollAnchor
    const tick = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 120)
    return () => window.clearTimeout(tick)
  }, [route.scrollAnchor])

  const navigate = (view: View, slug?: string) => {
    let nextRoute: Route
    if (view === 'lab') {
      nextRoute = { view, labId: slug ?? DEFAULT_LAB_ID }
    } else if (view === 'industries' && slug && INDUSTRY_SET.has(slug)) {
      nextRoute = { view, industryId: slug as IndustryId }
    } else if (view === 'industries') {
      // Bare industries → home page + scroll to industries section
      nextRoute = { view: 'home', scrollAnchor: 'industries' }
    } else {
      nextRoute = { view }
    }
    const target = routeToHash(nextRoute)
    if (window.location.hash !== target) {
      if (target === '') {
        history.pushState(null, '', window.location.pathname + window.location.search)
        setRouteState({ view: 'home' })
      } else {
        window.location.hash = target
      }
    }
    if (nextRoute.scrollAnchor) {
      // Defer so the route swap has settled, then scroll to the anchored section.
      window.setTimeout(() => {
        document.getElementById(nextRoute.scrollAnchor!)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 80)
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
    }
  }

  return (
    <OrionProvider>
      <AutoDemoProvider>
        <Shell route={route} onNavigate={navigate} />
      </AutoDemoProvider>
    </OrionProvider>
  )
}

function Shell({ route, onNavigate }: { route: Route; onNavigate: (v: View, slug?: string) => void }) {
  const t = useT()
  const autoDemo = useAutoDemo()
  const view = route.view

  useEffect(() => {
    document.title = t.meta.pageTitle
  }, [t.meta.pageTitle])

  // Stop the auto demo if the user navigates away from /demo.
  useEffect(() => {
    if (view !== 'demo' && autoDemo.isActive) {
      autoDemo.stop()
    }
  }, [view, autoDemo])

  return (
    <>
      <Header view={view} onNavigate={onNavigate} />
      {view === 'home' && (
        <HomePage
          onEnterDemo={() => onNavigate('demo')}
          onOpenIndustry={(id) => onNavigate('industries', id)}
        />
      )}
      {view === 'paradigms' && (
        <ParadigmsPage
          onEnterDemo={() => onNavigate('demo')}
          onOpenLab={(id) => onNavigate('lab', id)}
        />
      )}
      {view === 'method' && (
        <MethodPage
          onEnterDemo={() => onNavigate('demo')}
          onOpenLab={() => onNavigate('lab')}
          onOpenIndustries={() => onNavigate('industries')}
          onOpenPlaybook={() => onNavigate('playbook')}
        />
      )}
      {view === 'cases' && (
        <CasesPage onOpenPlaybook={() => onNavigate('playbook')} />
      )}
      {view === 'playbook' && (
        <PlaybookPage onBackToMethod={() => onNavigate('method')} />
      )}
      {view === 'demo' && <DemoPage />}
      {view === 'lab' && (
        <LabPage
          paradigmId={route.labId ?? DEFAULT_LAB_ID}
          onSelectParadigm={(id) => onNavigate('lab', id)}
          onBackToParadigms={() => onNavigate('paradigms')}
        />
      )}
      {view === 'industries' && route.industryId && (
        <IndustryDetailPage
          industryId={route.industryId}
          onBackToHub={() => onNavigate('industries')}
        />
      )}
      <footer className="mx-auto max-w-6xl px-6 sm:px-10 py-16 text-[color:var(--color-orion-haze)] text-[length:var(--text-small)]">
        <div className="dotted-rule mb-8" />
        <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
          <span>{t.footer.line}</span>
          <span className="mono">{t.footer.versionPrefix}{new Date().getFullYear()}</span>
        </div>
      </footer>
      {view === 'demo' && <AutoDemoOverlay />}
    </>
  )
}

function DemoPage() {
  const t = useT()
  const layers = useMemo<LayerStanza[]>(
    () => LAYER_IDS.map((id) => ({ id, ...t.layers[id] })),
    [t.layers],
  )
  return (
    <main>
      <Hero />
      <div className="mx-auto max-w-6xl px-6 sm:px-10">
        {layers.map((layer) => (
          <LayerSection key={layer.id} layer={layer} />
        ))}
      </div>
    </main>
  )
}
